import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { toRecorderLoggableError } from './recorder-errors';
import {
    CompositeFileRecorderOptions,
    CompositeRecorderBackend,
    CompositeRecorderStage,
    FileRecorder,
    FileRecorderClosePayload,
    FileRecorderCloseResult,
} from './types';

const logger = createLogger('recorder:composite');
const DEFAULT_PRIMARY_CLOSE_TIMEOUT_MS = 2000;
const DEFAULT_RECORDER_BACKEND: CompositeRecorderBackend = 'unknown';

export class CompositeFileRecorder implements FileRecorder {
    private readonly primary: FileRecorder;
    private readonly backup: FileRecorder;
    private readonly primaryBackend: CompositeRecorderBackend;
    private readonly backupBackend: CompositeRecorderBackend;
    private readonly primaryCloseTimeoutMs: number;

    constructor({
        primary,
        backup,
        primaryBackend = DEFAULT_RECORDER_BACKEND,
        backupBackend = DEFAULT_RECORDER_BACKEND,
        primaryCloseTimeoutMs = DEFAULT_PRIMARY_CLOSE_TIMEOUT_MS,
    }: CompositeFileRecorderOptions) {
        this.primary = primary;
        this.backup = backup;
        this.primaryBackend = primaryBackend;
        this.backupBackend = backupBackend;
        this.primaryCloseTimeoutMs = primaryCloseTimeoutMs;
    }

    private withCloseTimeout<T>(promise: Promise<T>) {
        return new Promise<T>((resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                reject(new Error('Primary recorder close timeout'));
            }, this.primaryCloseTimeoutMs);

            promise
                .then((result) => resolve(result))
                .catch((error) => reject(error))
                .finally(() => {
                    clearTimeout(timeoutId);
                });
        });
    }

    private logRecorderError(
        error: unknown,
        backend: CompositeRecorderBackend,
        stage: CompositeRecorderStage,
        method: string
    ) {
        logger.error(toRecorderLoggableError(error, { backend, stage }), { method });
    }

    async init(): Promise<void> {
        const [primaryResult, backupResult] = await Promise.allSettled([this.primary.init(), this.backup.init()]);

        if (primaryResult.status === 'rejected') {
            this.logRecorderError(primaryResult.reason, this.primaryBackend, 'init', 'init:primary');
        }
        if (backupResult.status === 'rejected') {
            this.logRecorderError(backupResult.reason, this.backupBackend, 'init', 'init:backup');
        }

        if (primaryResult.status === 'rejected' && backupResult.status === 'rejected') {
            throw primaryResult.reason;
        }
    }

    async addChunk(chunk: Blob): Promise<void> {
        const [primaryResult, backupResult] = await Promise.allSettled([
            this.primary.addChunk(chunk),
            this.backup.addChunk(chunk),
        ]);

        if (primaryResult.status === 'rejected') {
            this.logRecorderError(primaryResult.reason, this.primaryBackend, 'addChunk', 'addChunk:primary');
        }
        if (backupResult.status === 'rejected') {
            this.logRecorderError(backupResult.reason, this.backupBackend, 'addChunk', 'addChunk:backup');
        }

        if (primaryResult.status === 'rejected' && backupResult.status === 'rejected') {
            throw primaryResult.reason;
        }
    }

    async close(options?: FileRecorderClosePayload): Promise<FileRecorderCloseResult> {
        if (options?.withoutSave) {
            await Promise.allSettled([
                this.primary.close({ withoutSave: true }),
                this.backup.close({ withoutSave: true }),
            ]);
            return { status: 'discarded' };
        }

        if (options?.preserveForRecovery) {
            try {
                await this.withCloseTimeout(this.primary.close({ preserveForRecovery: true }));
            } catch (error) {
                this.logRecorderError(error, this.primaryBackend, 'close', 'close:primary-preserve');
            }

            try {
                return await this.backup.close({ preserveForRecovery: true });
            } catch (error) {
                this.logRecorderError(error, this.backupBackend, 'close', 'close:backup-preserve');
                return { status: 'discarded' };
            }
        }

        try {
            const primaryResult = await this.withCloseTimeout(this.primary.close(options));

            if (primaryResult.status === 'saved') {
                try {
                    await this.backup.close({ withoutSave: true });
                } catch (error) {
                    this.logRecorderError(error, this.backupBackend, 'close', 'close:backup-discard');
                }
                return primaryResult;
            }

            if (primaryResult.status === 'discarded') {
                await this.backup.close({ withoutSave: true });
                return primaryResult;
            }

            return this.backup.close({ preserveForRecovery: true });
        } catch (error) {
            this.logRecorderError(error, this.primaryBackend, 'close', 'close:primary');

            try {
                return await this.backup.close({ preserveForRecovery: true });
            } catch (backupError) {
                this.logRecorderError(backupError, this.backupBackend, 'close', 'close:backup');
                return { status: 'discarded' };
            }
        }
    }
}
